import React from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import DownloadButton from "./DownloadButton";

// New: Three-column wrapper for ReasonDisplay (reserved for layout)
/* eslint-disable-next-line no-unused-vars */
const ReasonGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 260px) minmax(0, 700px) minmax(
      180px,
      320px
    );
  align-items: left;
  width: 100%;
  height: 100%;
  @media (max-width: 1100px) {
    grid-template-columns: minmax(120px, 180px) minmax(0, 480px) minmax(
        120px,
        200px
      );
  }
  @media (max-width: 900px) {
    grid-template-columns: minmax(80px, 120px) minmax(0, 340px) minmax(
        80px,
        120px
      );
  }
`;

/* eslint-disable-next-line no-unused-vars */
const LeftPlaceholder = styled.div`
  grid-column: 1 / 2;
`;

const ReasonContainer = styled.div`
  grid-column: 2 / 3;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: transparent;
  padding: 0;
  margin: 0;
`;

const ReasonContent = styled(motion.div).withConfig({
  shouldForwardProp: prop => prop !== "hasDescription",
})`
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: left;
  @media (max-width: 1100px) {
    width: 420px;
    min-width: 420px;
    padding: 0 1rem;
  }
  @media (max-width: 900px) {
    width: 340px;
    min-width: 340px;
    padding: 0 0.5rem;
  }
`;

const ReasonTitle = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 900;
  font-family: "Inter", sans-serif;
  font-style: italic;
  margin-bottom: 1.5rem;
  color: white;
  line-height: 1.1;
  display: inline-block;
  text-shadow:
    1px 0 #00ff00,
    0 1px #00ff00;
  @media (max-width: 1100px) {
    font-size: 2.3rem;
  }
  @media (max-width: 900px) {
    font-size: 1.6rem;
  }
`;

const ReasonDescription = styled(motion.p)`
  font-size: 1.4rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 1);
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-style: italic;
  @media (max-width: 1100px) {
    font-size: 1.1rem;
  }
  @media (max-width: 900px) {
    font-size: 0.95rem;
  }
`;

/* eslint-disable-next-line no-unused-vars */
const PhoneImage = styled(motion.div)`
  grid-column: 3 / 4;
  width: 280px;
  height: 560px;
  background-image: url("/phone-mockup.png");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  margin: 0 auto;
  @media (max-width: 1100px) {
    width: 180px;
    height: 360px;
  }
  @media (max-width: 900px) {
    width: 120px;
    height: 240px;
  }
`;

const reasons = {
  1: {
    title: "First rule of health club? Check yourself!",
    description:
      "Schedule comprehensive health screenings with doorstep sample collection. Stay ahead of potential health issues without leaving home - because prevention beats panic every time.",
    image: "/42 reasons images/Book Comprehensive Health Checkups.webp",
  },
  2: {
    title: "It takes TWO to tango",
    description:
      "Why waste time on Dr. Wrong? Our AI analyzes your symptoms and connects you with specialists who actually get you. It's like Tinder, but for healthcare.",
    image: "/42 reasons images/37 - Find the right doctor.webp",
  },
  3: {
    title: "Point. Shoot. Approved.",
    description:
      "Here's the thing - you've got better things to do than triple-check forms. Our AI is literally built for this. Use it. Thank us later.",
    image: "/42 reasons images/14 AI errors.webp",
  },
  4: {
    title: "Four-sight into your medical bills",
    description:
      "See the future (of your hospital bill) before it happens. Get accurate cost estimates so the only shock you get is how prepared you are.",
    image: "/42 reasons images/Cost of Ailment.webp",
  },
  5: {
    title: "Five-star specialists at your fingertips",
    description:
      "Dermatologists, cardiologists, pulmonologists - basically if it ends in '-ologist', we've got the best one waiting.",
    image: "/42 reasons images/42 - Consult specialists.webp",
  },
  6: {
    title: "Your six-pack starter pack",
    description:
      "Get into top-tier gyms and fitness studios nationwide. We give you the best gym benefits so you can crush your fitness goals.",
    image: "/42 reasons images/cult.webp",
  },
  7: {
    title: "The seventh wonder: Insurance that makes sense",
    description:
      "Co-payment? Deductible? Sub-limit? Ask about your policy, get answers you can actually understand",
    image: "/42 reasons images/18 - Understand your policy in english.webp",
  },
  8: {
    title: "Eight AM or PM, doctors on demand",
    description:
      "When you're sick, you need help. Not forms. Not waiting. Just doctors. Healthcare made simple.",
    image: "/42 reasons images/Connect with Plum Telehealth.webp",
  },
  9: {
    title: "Your wellness wingman",
    description:
      "Introduced you to that cute gym membership, then made sure you had everything for the relationship to work. Protein for gains, activewear for style, healthy snacks for sustenance. We're invested in your success story",
    image: "/42 reasons images/Perks.webp",
  },
  10: {
    title: "Family: can't choose them, but you can cover them",
    description:
      "Your dad's political takes? Questionable. His need for healthcare coverage? Unquestionable. Add family members even when they're being... themselves.",
    image: "/42 reasons images/7 Enrollment.webp",
  },
  11: {
    title: "Be the family know-it-all",
    description:
      "Know exactly who's covered and how much with a quick tap. No more guessing games or awkward 'did you renew your policy?' conversations at family dinners.",
    image: "/42 reasons images/whos covered.webp",
  },
  12: {
    title: "Your health card had a glow-up",
    description:
      "Remember fumbling through your wallet at the hospital counter? Yeah, we fixed that. Download your digital health ID and never again be that person holding up the line while digging through receipts and expired coupons.",
    image: "/42 reasons images/Access Your Insurer Details & Health ID.webp",
  },
  13: {
    title: "Plot twist: Thirteen is your lucky number",
    description:
      "Track your claim in real-time instead of wondering if it vanished into the healthcare void. Every step, every update, right on your phone. Turns out 13 brings good luck when it comes to transparency.",
    image: "/42 reasons images/10 Cashless status.webp",
  },
  14: {
    title: "Your phone camera just became profitable",
    description:
      "Take a photo, get your money back. It's like Instagram, but instead of likes, you get actual cash. Finally, a social media platform that pays dividends.",
    image: "/42 reasons images/9 Reimbursements.webp",
  },
  15: {
    title: "Fifteen megawatt smile guaranteed",
    description:
      "Your teeth and eyes do all the heavy lifting in first impressions. Might as well give them premium coverage so they can keep being your best wingmen.",
    image: "/42 reasons images/dental.webp",
  },
  16: {
    title: "Sweet sixteen and never paying upfront",
    description:
      "Walk into any network hospital and get treated without touching your wallet. It's like being sixteen again.",
    image: "/42 reasons images/8 Cashless claims.webp",
  },
  17: {
    title: "Seventeen steps ahead of 'where's my money?",
    description:
      "Check your reimbursement status anytime with real-time updates. No more wondering if your claim disappeared into the insurance void.",
    image: "/42 reasons images/reimbursement_status.webp",
  },
  18: {
    title: "Find hospitals that won't bankrupt you",
    description:
      "Locate nearby hospitals where you can get cashless treatment. GPS directions included, because emergencies don't wait for you to figure out which hospitals take your insurance.",
    image: "/42 reasons images/13 Network Hospitals.webp",
  },
  19: {
    title: "Your pharmacist's least favorite app",
    description:
      "Get serious discounts on prescriptions through our partnerships. Finally, a reason to smile when picking up your monthly medications instead of wincing at the receipt.",
    image: "/42 reasons images/Medicines.webp",
  },
  20: {
    title: "Your health diary, minus the drama",
    description:
      "Every claim, every date, perfectly organised. Great for taxes, medical records, or proving to your spouse that yes, you really did go to the doctor like you said you would.",
    image: "/42 reasons images/12 Claims History.webp",
  },
  21: {
    title: "Finally 21, time for VIP treatment",
    description:
      "Award-winning support that treats you like you matter. Real humans who solve your problems quickly and efficiently.",
    image: "/42 reasons images/15 - VIP support.webp",
  },
  22: {
    title: "Your 3am thoughts have backup now",
    description:
      "Licensed therapists available around the clock through secure calls. Because mental health crises don't keep business hours.",
    image: "/42 reasons images/mentalhelpHelpline.webp",
  },
  23: {
    title: "Thirty-two ways to not Google your symptoms",
    description:
      "Bite-sized health wisdom from actual experts, delivered regularly. Stay informed without falling down WebMD rabbit holes that convince you you're dying.",
    image: "/42 reasons images/HealthStory.webp",
  },
  24: {
    title: "Health wisdom that's pure 24-carat gold",
    description:
      "Expert-curated articles that don't require a medical degree to understand. Stay informed without drowning in medical jargon.",
    image: "/42 reasons images/19 - Articles on health.webp",
  },
  25: {
    title: "Health is personal",
    description:
      "There's no one-size-fits-all when it comes to your body. Get insights tailored to your unique health data because you deserve recommendations that actually fit your life.",
    image: "/42 reasons images/21 Personalised insights.webp",
  },
  26: {
    title: "The wellness cherry on top",
    description:
      "On top of everything else, discover wellness brands that actually help you live healthier. From fitness tech to organic nutrition - because comprehensive health support includes the tools to succeed.",
    image: "/42 reasons images/Perks.webp",
  },
  27: {
    title: "Weekly doses of expert wisdom",
    description:
      "Weekly wellness sessions with actual experts who know what they're talking about. Stay informed, fit, and healthy without the gym membership fees.",
    image: "/42 reasons images/Join Live Wellness Events.webp",
  },
  28: {
    title: "Healthy snacks that taste good",
    description:
      "Nutritious options that actually taste good and support your wellness journey. Because eating well shouldn't feel like punishment.",
    image: "/42 reasons images/tastySnacks.webp",
  },
  29: {
    title: "Know your health status in minutes",
    description:
      "Science-backed health assessments that provide valuable insights quickly and accurately. Get a clear picture of your wellness without the guesswork.",
    image: "/42 reasons images/Assessments.webp",
  },
  30: {
    title: "Insurance that fits like a good pair of jeans",
    description:
      "Health plans tailored to your actual needs, lifestyle, and budget. Because cookie-cutter insurance works about as well as cookie-cutter jeans.",
    image: "/42 reasons images/Personal.webp",
  },
  31: {
    title: "Your insurance control center",
    description:
      "Keep track of all your insurance policies - from health to life insurance, everything organized beautifully in one place.",
    image: "/42 reasons images/Assessments.webp",
  },
  32: {
    title: "Skip the queue, get verified online",
    description:
      "Quick digital verification that doesn't require scanning forty documents. Upload what you need, get verified, move on with your life.",
    image: "/42 reasons images/Pre-filled KYC.webp",
  },
  33: {
    title: "4.8 stars don't lie",
    description:
      "Thousands of users rated us 4.8 on App Store and 4.9 on Play Store. Sometimes the best marketing is just letting happy customers do the talking.",
    image: "/42 reasons images/appRating.webp",
  },
  34: {
    title: "Fill the gaps your HR didn't mention",
    description:
      "Add extra protection on top of your company insurance. Because corporate coverage is great until you actually need comprehensive care.",
    image: "/42 reasons images/STU.webp",
  },
  35: {
    title: "What's it gonna take, thirty-six reasons?",
    description:
      "If you're reading this, you're either incredibly thorough or desperately avoiding work. Either way, we're impressed. Seven more reasons to go, you beautiful, curious creature.",
    image: "/42 reasons images/reason35.webp",
  },
  36: {
    title: "Protect what matters most",
    description:
      "Term life insurance that protects your loved ones financially. Because their security matters more than your Netflix subscription, even if it costs about the same.",
    image: "/42 reasons images/Term life.webp",
  },
  37: {
    title: "Instant bank account verification",
    description:
      "Verify your account with secure penny drop technology - no documents, no delays. Just seamless digital verification for faster claim settlements.",
    image: "/42 reasons images/RI claims.webp",
  },
  38: {
    title: "Your health conversations, completely private",
    description:
      "Your consultation is completely private - even from other family members. Because some health conversations are meant for doctor's ears only.",
    image: "/42 reasons images/private-telehealth.webp",
  },
  39: {
    title: "Flash sales for your flash abs",
    description:
      "Exclusive Drop deals and special offers on health products and services. Savings available only to Plum users, because loyalty should have its perks.",
    image: "/42 reasons images/Drops.webp",
  },
  40: {
    title: "Professional therapy, personal space",
    description:
      "Connect with licensed therapists through secure video sessions. Your mental health matters as much as your physical health, and both deserve professional care.",
    image: "/42 reasons images/Mental Health.webp",
  },
  41: {
    title: "Paperwork-free reimbursements",
    description:
      "Skip the paperwork - just photograph your OPD receipts and get reimbursed quickly. Because outpatient treatments shouldn't come with homework.",
    image: "/42 reasons images/Plum OPD.webp",
  },
  42: {
    title: "Saved the best for last: family support",
    description:
      "Give your entire family access to all these amazing features. Because the best things in life - and health - are meant to be shared with the people you love most.",
    image: "/42 reasons images/DependentLogin.webp",
  },
};

// Add a function to get the reason data
export const getReasonData = reasonNumber => {
  return reasons[reasonNumber];
};

const ReasonDisplay = ({ reasonNumber, onOpenQRDialog }) => {
  if (reasonNumber === 0) {
    return (
      <ReasonContainer>
        <AnimatePresence mode="wait">
          <ReasonContent
            key={0}
            hasDescription={false}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <ReasonTitle>42 Reasons To Download The Plum App</ReasonTitle>
          </ReasonContent>
        </AnimatePresence>
      </ReasonContainer>
    );
  }

  const reason = reasons[reasonNumber];
  const hasDescription = reason.description && reason.description.trim() !== "";

  return (
    <ReasonContainer>
      <AnimatePresence mode="wait">
        <ReasonContent
          key={reasonNumber}
          hasDescription={hasDescription}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          <ReasonTitle>{reason.title}</ReasonTitle>
          {hasDescription && (
            <ReasonDescription>{reason.description}</ReasonDescription>
          )}
          <DownloadButton onOpenQRDialog={onOpenQRDialog} />
        </ReasonContent>
      </AnimatePresence>
    </ReasonContainer>
  );
};

export default ReasonDisplay;
